import * as React from "react";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import ValidateAccessHOC from "@market/components/ValidateAccessHOC/ValidateAccessHOC";
import Loader from "@appComponents/Loader/Loader";
import Header from "@appComponents/Header/Header";
import StepsHeader from "@market/components/StepsHeader/StepsHeader";
import Summary from "@market/components/Summary/Summary";
import TradePricing from "@market/components/TradePricing/TradePricing";
import AgreementContainer from "@market/components/AgreementContainer/AgreementContainer";
import Big from "big.js";
import ISellTradePage, { TSellTradePage } from "./ISellTradePage";
import {
  agreementsForTradeSell,
  riskWarningsForTradeSell as riskWarnings,
} from "@market/components/AgreementContainer/agreements";
import { connect } from "react-redux";
import { catchError } from "@features/utils/functions";
import { CallApiOnRender } from "@features/utils/reactFunctions";
import { setAssetDetails } from "@market/features/action.market";
import {
  setToggleAutomatically,
  getDiscountFeePercent,
  getTransactionFee,
} from "@market/components/PaymentDetails/functions";
import { IPaymentDetailsState } from "@market/components/PaymentDetails/IPaymentDetails";
import ConvertShares from "@market/components/ConvertShares/ConvertShares";
import ISPV from "@market/components/SPVsList/ISPV";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import Confirmation from "@appComponents/Confirmation/Confirmation";
import onlyLoggedInHOC from "@appComponents/OnlyLoggedInHOC/OnlyLoggedIn";
import PaymentDetails from "@market/components/PaymentDetails/PaymentDetails";
import ISPVsAsset from "@market/interfaces/ISPVsAsset";

import * as SMAppStyle from "@market/SMAppStyle.module.scss";

const sectionNames = ["Select Amount", "Agreements", "Summary"];

const stepsAmount = 3;

export class SellTradePage extends React.Component<
  ISellTradePage,
  TSellTradePage
> {
  state: TSellTradePage = {
    step: 1,
    loading: true,
    nextStepEnable: false,
    successPage: false,
    checks: [],
    numberOfSharesInput: 0,
    totalValue: 0,
    bft_fee: false,
    validationMessages: [],
    orderDetails: {
      id: 0,
      status: "",
      numberOfShares: 0,
      pricePerShare: 0,
      assetId: 0,
      investorId: 0,
    },
    convertShares: false,
    paymentDetails: {
      fee: 0,
      discountIsOn: false,
      toggleTouched: false,
      toggleBlocked: false,
      discountFeePercent: 0,
      bftExchange: 0,
    },
  };

  private big = Big();

  componentDidMount() {
    const { match } = this.props;
    const assetId = this.props.spv.trading_asset_id;
    const orderId = match.params.order_id;

    Promise.all([
      SecondaryMarketService.getAssetOrder(assetId, orderId),
      SecondaryMarketService.getExchange(),
    ])
      .then((resp) => {
        if (this.big(resp[0].data.number_of_available_shares).eq(0)) {
          this.props.history.push(`/trades/market`);
        } else {
          const numberOfShares = Number(
            resp[0].data.number_of_available_shares
          );
          const pricePerShare = Number(resp[0].data.price);

          this.setState({
            loading: false,
            orderDetails: {
              id: resp[0].data.id,
              status: resp[0].data.status,
              numberOfShares: numberOfShares,
              pricePerShare: pricePerShare,
              assetId: assetId,
              investorId: resp[0].data.investor_id,
              tradePartner: resp[0].data.investor,
            },
            paymentDetails: {
              ...this.state.paymentDetails,
              bftExchange: resp[1].data.usd_bft,
              tradePartner: resp[0].data.investor,
            },
          });
        }
      })
      .catch((err) => {
        this.props.history.push(`/error/${err.response.status}`);
      });
  }

  calculatePaymentDetails = (
    paymentDetails: TSellTradePage["paymentDetails"],
    totalValue: number
  ) => {
    let fee;
    const { calculatedFee, calculatedDiscountIsOn, toggleBlocked } =
      setToggleAutomatically(
        getDiscountFeePercent(true, this.props.marketSettings, true),
        paymentDetails.discountIsOn,
        paymentDetails.toggleTouched,
        paymentDetails.bftExchange,
        totalValue,
        this.props.userWallets.bftWallet.balance
      );
    const discountFeePercent = getDiscountFeePercent(
      calculatedDiscountIsOn,
      this.props.marketSettings
    );

    if (!calculatedFee) {
      fee = getTransactionFee(
        discountFeePercent,
        calculatedDiscountIsOn,
        totalValue,
        paymentDetails.bftExchange
      );
    } else {
      fee = calculatedFee;
    }

    return {
      ...paymentDetails,
      fee,
      discountFeePercent,
      toggleBlocked,
      discountIsOn: calculatedDiscountIsOn,
    };
  };

  updateState = (values: {
    [key: string]: Partial<TSellTradePage | IPaymentDetailsState>;
  }) => {
    const messages = this.validation(values);
    const nextStepEnable = messages.length === 0;
    const paymentDetails =
      Object.keys(values)[0] === "paymentDetails"
        ? this.calculatePaymentDetails(
            values.paymentDetails as IPaymentDetailsState,
            this.state.totalValue
          )
        : this.calculatePaymentDetails(
            this.state.paymentDetails,
            (values.totalValue || this.state.totalValue) as number
          );

    this.setState({
      ...values,
      nextStepEnable,
      paymentDetails,
      bft_fee: paymentDetails.discountIsOn,
      validationMessages: messages,
    });
  };

  prevStep = () => {
    if (this.state.step > 1) {
      this.setState({ step: this.state.step - 1, nextStepEnable: true });
      window.scrollTo(0, 0);
    }
  };

  nextStep = () => {
    if (this.state.step < stepsAmount) {
      this.setState({
        step: this.state.step + 1,
        nextStepEnable:
          this.validation({ step: this.state.step + 1 }).length === 0,
      });
    } else {
      this.setState({ nextStepEnable: false });

      const data = {
        number_of_shares: Number(this.state.numberOfSharesInput),
        bft_fee: this.state.bft_fee,
        share_transfer_agreement:
          this.state.checks.length ===
          agreementsForTradeSell.length + riskWarnings.length,
        convert_shares: this.state.convertShares ? true : undefined,
      };

      SecondaryMarketService.postSellTrade(
        this.props.match.params.order_id,
        data
      )
        .then((res) => {
          this.setState({
            successPage: true,
          });
        })
        .catch((err) => {
          catchError(err);
        });
    }

    window.scrollTo(0, 0);
  };

  validation = (values: {} = {}) => {
    const tempState = { ...this.state, ...values };
    const minimumTradableValue =
      this.props.marketSettings.minimum_tradable_value;
    const warnings: string[] = [];
    switch (tempState.step) {
      case 1:
        if (
          this.convertSharesValidation(tempState) &&
          !tempState.convertShares
        ) {
          warnings.push("convert_shares");
        }
        if (this.big(minimumTradableValue).gt(tempState.totalValue)) {
          warnings.push(`Your requested value is below the minimum threshold
          of ${minimumTradableValue} USD for this asset`);
        } else if (
          Number(tempState.numberOfSharesInput) !==
            tempState.orderDetails.numberOfShares &&
          Number(
            this.big(tempState.orderDetails.numberOfShares)
              .times(tempState.orderDetails.pricePerShare)
              .minus(tempState.totalValue)
              .toFixed(4)
          ) < minimumTradableValue
        ) {
          warnings.push(`Your requested amount is too high due to the remainder being
            less than the minimum transaction threshold for this order
            (${minimumTradableValue} USD).`);
        }
        return warnings;

      case 2:
        if (
          tempState.checks.length !==
          agreementsForTradeSell.length + riskWarnings.length
        ) {
          warnings.push("Please accept all agreements");
        }
        return warnings;
    }

    return warnings;
  };

  convertSharesValidation = (state: TSellTradePage = this.state) => {
    return this.big(Number(state.numberOfSharesInput) || 0).gt(
      this.getTradingShares()
    );
  };

  displayStep = () => {
    const userAssetsShares = this.getAssetsShares();
    const maxAvailableShares =
      userAssetsShares < this.state.orderDetails.numberOfShares
        ? userAssetsShares
        : this.state.orderDetails.numberOfShares;
    const orderIsSmaller =
      userAssetsShares > this.state.orderDetails.numberOfShares;

    switch (this.state.step) {
      case 1:
      default:
        return (
          <>
            <TradePricing
              numberOfSharesInput={this.state.numberOfSharesInput}
              totalOrderValue={this.state.totalValue}
              pricePerShare={this.state.orderDetails.pricePerShare}
              updateState={this.updateState}
              orderSharesAmount={maxAvailableShares}
              fillOrderButton={orderIsSmaller}
            />
            <div className={SMAppStyle.converter}>
              {this.convertSharesValidation() && this.displaySharesConverter()}
            </div>
          </>
        );
      case 2:
        return (
          <AgreementContainer
            updateState={this.updateState}
            action={"SellTrade"}
            checks={this.state.checks}
            documentData={{
              assetId: this.props.spv.trading_asset_id,
              orderData: {
                numberOfShares: this.state.numberOfSharesInput,
                pricePerShare: this.state.orderDetails.pricePerShare,
              },
            }}
          />
        );
      case 3:
        return (
          <Summary
            userWallets={this.props.userWallets}
            spvName={this.props.spv.name}
            paymentDetails={this.state.paymentDetails}
            totalValue={this.state.totalValue}
            isSellPage={true}
            isOrderType={false}
          />
        );
    }
  };

  displayWarnings = () => {
    if (this.state.validationMessages.includes("convert_shares")) {
      return null;
    }
    if (this.state.numberOfSharesInput) {
      return (
        <div className={SMAppStyle.warning}>
          {this.state.validationMessages.map((el, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: el }} />
          ))}
        </div>
      );
    }
    return null;
  };

  displayButtons = () => {
    const { nextStepEnable, convertShares, step } = this.state;

    return (
      <StepButtons
        isFirstStep={this.state.step === 1}
        nextStepEnable={nextStepEnable}
        nextStep={this.nextStep}
        prevStep={this.prevStep}
        nextText={
          step === 3
            ? `Sell${convertShares ? " & Convert Shares" : ""}`
            : "Next"
        }
      />
    );
  };

  displaySharesConverter = () => {
    return (
      <ConvertShares
        assets={this.props.spv!.orderShares!.assets}
        value={this.state.convertShares}
        setCheckbox={this.updateState}
        type="trade"
      />
    );
  };

  successPage = () => {
    const link = `/companies/${this.props.spv.company_slug}/market/spvs/${this.props.spv.id}/orders`;

    const data = {
      title: "Your trade is confirmed and finished.",
      texts: [
        `Congratulations. Shares transfer has been completed and you can find your new funds in your wallet`,
      ],
      link1: {
        text: "Go to my trades",
        url: "/market/my_trades",
      },
      link2: {
        text: "Browse other offers",
        url: `${link}`,
      },
    };

    return (
      <CallApiOnRender
        api={() => {
          this.props.setAssetDetails(
            this.props.spv.trading_asset_id,
            this.props.spv.id
          );
        }}
      >
        <Confirmation data={data} />;
      </CallApiOnRender>
    );
  };

  renderContent = () => {
    return (
      <>
        <main className={SMAppStyle.left}>
          <StepsHeader steps={sectionNames} current={this.state.step} />
          {this.displayStep()}
          {this.displayWarnings()}
          {this.displayButtons()}
        </main>
        <aside className={SMAppStyle.right}>
          <PaymentDetails
            isSellPage={true}
            spv={this.props.spv}
            orderedNumberOfShares={this.state.numberOfSharesInput}
            orderedPricePerShare={this.state.orderDetails.pricePerShare}
            totalValue={this.state.totalValue}
            updateState={this.updateState}
            paymentDetails={this.state.paymentDetails}
            bftFeeDiscountPercent={
              this.props.marketSettings.bft_fee_discount_percent
            }
            isBlocked={this.state.step >= 3}
          />
        </aside>
      </>
    );
  };

  getAssetsShares = () => {
    if (this.props.spv.orderShares) {
      return this.props.spv.orderShares.assets.reduce(
        (acc: number, curr: ISPVsAsset) => {
          return Number(this.big(acc).add(curr.sellable_number_of_shares));
        },
        0
      );
    }

    return 0;
  };

  getTradingShares = () => {
    const tradingAsset = this.props.spv!.orderShares!.assets.find(
      (el: ISPVsAsset) => el.trading
    );
    return tradingAsset ? tradingAsset.sellable_number_of_shares : 0;
  };

  render() {
    return this.state.loading && this.props.spv ? (
      <Loader moreSpace={true} />
    ) : (
      <>
        <Header padding={"50 0"}>
          <h1>Sell Shares</h1>
        </Header>
        <div className={SMAppStyle.container}>
          {this.state.successPage ? this.successPage() : this.renderContent()}
        </div>
      </>
    );
  }
}

const mapStateToProps = (store: any, ownProps: ISellTradePage) => {
  return {
    marketSettings: store.secondaryMarketReducer.marketSettings,
    userInfo: store.userInfo,
    spv: store.secondaryMarketReducer.spvs
      ? store.secondaryMarketReducer.spvs.find(
          (spv: ISPV) => spv.id === Number(ownProps.match.params.asset_id)
        )
      : store.secondaryMarketReducer.spvs,
    userWallets: store.secondaryMarketReducer.userWallets,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    setAssetDetails: (assetId: number, spvId: number) => {
      dispatch(setAssetDetails(assetId, spvId));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(onlyLoggedInHOC(ValidateAccessHOC(SellTradePage)));
